import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { PageShell, PillarSection, TransparencyNote } from "@/components/PageShell";
import { TravelSection } from "@/components/TravelSection";
import { Reveal } from "@/components/Reveal";
import { ConsultationModeBadge } from "@/components/ConsultationModeBadge";

// Visuals — créations originales FACOW360 (Alchimie Digitale)
const IMG_ANALYSE = "https://customer-assets.emergentagent.com/job_ai-vivant/artifacts/7uru3sk8_6.jpg";          // praticien + antenne dans bâtiment ancien
const IMG_HABITAT = "https://customer-assets.emergentagent.com/job_ai-vivant/artifacts/xfswt7jy_geobiologie.jpg"; // table en pierre, fleur de vie, géométrie sacrée
const IMG_NATURE_1 = "https://customer-assets.emergentagent.com/job_ai-vivant/artifacts/gc00uas2_Sans%20titre3.jpg"; // mégalithes + pilier d'ancrage
const IMG_NATURE_2 = "https://customer-assets.emergentagent.com/job_ai-vivant/artifacts/eiglprz2_5.jpg";          // lac & montagnes

const HeroVisual = ({ src, alt, caption, testId }) => (
  <Reveal direction="zoom" duration={1.1} amount={0.25}>
    <figure
      className="relative overflow-hidden rounded-3xl border border-[#D4AF37]/30 bg-[#0F1A3A] mt-8 group"
      data-testid={testId}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0F1A3A] via-transparent to-transparent opacity-70" />
      <figcaption className="absolute bottom-0 left-0 right-0 px-6 py-4">
        <span className="inline-block px-3 py-1.5 rounded-full bg-[#0F1A3A]/85 backdrop-blur-sm border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] font-mono-tech uppercase tracking-[0.25em]">
          {caption}
        </span>
      </figcaption>
    </figure>
  </Reveal>
);

export default function Geobiologie() {
  const { locale, t } = useLang();
  const isFr = locale === "fr";

  return (
    <PageShell
      testId="page-geobiologie"
      title={isFr ? "Géobiologie de l'habitat" : "Habitat Geobiology"}
      subtitle={isFr
        ? "L'étude précise de l'influence de l'environnement sur le vivant — humains, animaux, plantes — pour transformer un lieu en espace régénérateur. Une approche de consultant technique haut de gamme, ancrée dans l'Alchimie Digitale FACOW360."
        : "The precise study of the environment's influence on living beings — humans, animals, plants — to turn a place into a regenerating space. A premium technical-consulting approach, anchored in FACOW360 Digital Alchemy."}
    >
      <ConsultationModeBadge isFr={isFr} testId="geo-consultation-badge" />

      {/* ============================ MOVEMENT 1 — ANALYSE TELLURIQUE ============================ */}

      <Reveal direction="up" amount={0.2}>
        <PillarSection
          heading={isFr ? "I — Analyse Tellurique" : "I — Telluric Analysis"}
          testId="geo-section-analyse"
        >
          <p>
            {isFr
              ? "La phase d'analyse tellurique est le préalable à toute intervention FACOW360. Sur place, j'opère une cartographie rigoureuse des champs invisibles qui traversent votre lieu de vie ou de travail : réseaux globaux (Hartmann · Peyré · Palm), réseaux diagonaux (Curry · Wissmann), failles, veines d'eau souterraines, croisements géopathogènes."
              : "Telluric analysis is the prerequisite of every FACOW360 intervention. On-site, I map rigorously the invisible fields that cross your living or working space: global grids (Hartmann · Peyré · Palm), diagonal grids (Curry · Wissmann), faults, underground water veins, geopathogenic crossings."}
          </p>
          <p>
            {isFr
              ? "L'instrumentation — antennes calibrées, capteurs hyperfréquences, analyseur de champ électromagnétique — sert un seul objectif : transformer l'invisible en données exploitables, pour ensuite calibrer la juste réponse vibratoire."
              : "The instrumentation — calibrated antennas, high-frequency sensors, electromagnetic field analyser — serves a single purpose: turning the invisible into exploitable data, so I can then calibrate the right vibrational response."}
          </p>
        </PillarSection>
      </Reveal>

      <HeroVisual
        src={IMG_ANALYSE}
        alt={isFr
          ? "Analyse tellurique FACOW360 — détection des réseaux Hartmann et Curry dans un bâtiment ancien"
          : "FACOW360 telluric analysis — Hartmann and Curry grid detection in an ancient building"}
        caption={isFr ? "01 · Analyse tellurique in situ" : "01 · On-site telluric analysis"}
        testId="geo-visual-analyse"
      />

      <PillarSection
        heading={isFr ? "Les réseaux énergétiques de la Terre" : "The Earth's energetic grids"}
        testId="geo-section-grids"
      >
        <p>
          {isFr
            ? "La Terre est parcourue par des rayonnements énergétiques formant des maillages géométriques précis. On distingue les réseaux globaux : le Réseau Hartmann (Nickel), le Réseau Peyré (Or), le Réseau Palm (Cuivre) ; et les réseaux diagonaux : le Réseau Curry (Fer) et le Réseau Wissmann (Aluminium)."
            : "The Earth is crossed by energetic radiations forming precise geometric grids. The global grids include the Hartmann network (Nickel), the Peyré network (Gold) and the Palm network (Copper); the diagonal grids include the Curry network (Iron) and the Wissmann network (Aluminium)."}
        </p>
        <p>
          {isFr
            ? "Le point sensible n'est pas le réseau en lui-même, mais ses superpositions : un nœud Hartmann sur une faille, une veine d'eau sous une zone de repos. C'est précisément là que se concentre mon expertise."
            : "The sensitive point is not the grid itself, but its superpositions: a Hartmann node above a fault, a water vein under a resting area. This is precisely where my expertise focuses."}
        </p>
      </PillarSection>

      {/* ============================ MOVEMENT 2 — HARMONISATION DE L'HABITAT ============================ */}

      <PillarSection
        heading={isFr ? "II — Harmonisation de l'Habitat" : "II — Habitat Harmonisation"}
        testId="geo-section-habitat"
      >
        <p>
          {isFr
            ? "Une fois l'analyse complète, vient la phase de calibrage. À l'intérieur, j'utilise la géométrie sacrée correctionnelle pour rééquilibrer les espaces sans recourir à des méthodes artificielles. Les 5 Solides de Platon — Tétraèdre, Hexaèdre, Octaèdre, Icosaèdre, Dodécaèdre — jouent un rôle d'ancrage et d'harmonisation des fréquences."
            : "Once the analysis is complete, the calibration phase begins. Indoors, I use corrective sacred geometry to rebalance spaces without resorting to artificial methods. The 5 Platonic solids — Tetrahedron, Hexahedron, Octahedron, Icosahedron, Dodecahedron — anchor and harmonise frequencies."}
        </p>
        <p>
          {isFr
            ? "Tout matériau possède une capacité de mémorisation vibratoire — la mémoire des murs. L'harmonisation de l'habitat consiste à effacer ces empreintes lourdes et à réinstaller une fréquence claire, cohérente avec la vie qui s'y déploie."
            : "Every material has a vibrational memory capacity — the memory of walls. Harmonising the habitat means erasing heavy imprints and reinstalling a clear frequency, coherent with the life unfolding there."}
        </p>
      </PillarSection>

      <HeroVisual
        src={IMG_HABITAT}
        alt={isFr
          ? "Calibrage de l'habitat FACOW360 — table en marbre, géométrie sacrée correctionnelle, fleur de vie"
          : "FACOW360 habitat calibration — marble table, corrective sacred geometry, flower of life"}
        caption={isFr ? "02 · Géométrie sacrée correctionnelle" : "02 · Corrective sacred geometry"}
        testId="geo-visual-habitat"
      />

      <PillarSection
        heading={isFr ? "Failles, eaux souterraines & pollutions technologiques" : "Faults, underground water & technological pollution"}
        testId="geo-section-faults"
      >
        <p>
          {isFr
            ? "Les discontinuités du sol — failles sèches, cavités, eaux souterraines — modifient le champ magnétique local et peuvent perturber la sécrétion de mélatonine, affaiblissant la qualité du sommeil et l'immunité."
            : "Soil discontinuities — dry faults, cavities, underground water — alter the local magnetic field and may disrupt melatonin secretion, weakening sleep quality and immunity."}
        </p>
        <p>
          {isFr
            ? "Je mesure également les pollutions technologiques : champs électriques, magnétiques et hyperfréquences (Wi-Fi, 5G, antennes-relais), responsables d'un stress oxydatif chronique. L'objectif n'est jamais de tout supprimer, mais de redessiner l'usage des espaces autour des zones les plus saines."
            : "I also measure technological pollution: electric, magnetic and high-frequency fields (Wi-Fi, 5G, relay antennas), responsible for chronic oxidative stress. The goal is never to remove everything, but to redesign space usage around the healthiest zones."}
        </p>
      </PillarSection>

      {/* ============================ MOVEMENT 3 — GÉOBIOLOGIE DE LA NATURE ============================ */}

      <PillarSection
        heading={isFr ? "III — Géobiologie de la Nature" : "III — Geobiology of Nature"}
        testId="geo-section-nature"
      >
        <p>
          {isFr
            ? "Le terrain naturel est le grand maître. Forêts, mégalithes, sources, lacs : ces lieux portent une signature vibratoire ancienne, souvent calibrée par nos ancêtres bâtisseurs. Mon travail consiste à lire ces signatures, à identifier les vortex et les piliers d'ancrage tellurique, puis à stabiliser les fréquences dans l'environnement immédiat."
            : "The natural terrain is the great master. Forests, megaliths, springs, lakes: these places carry an ancient vibrational signature, often calibrated by our ancestral builders. My role is to read these signatures, identify the telluric anchor pillars and vortices, then stabilise the frequencies in the immediate environment."}
        </p>
        <p>
          {isFr
            ? "Cette approche est particulièrement adaptée aux domaines, sites mégalithiques, propriétés rurales et écolieux qui souhaitent retrouver leur cohérence vibratoire originelle."
            : "This approach is particularly suited to estates, megalithic sites, rural properties and eco-places that wish to reconnect with their original vibrational coherence."}
        </p>
      </PillarSection>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8 max-w-5xl mx-auto" data-testid="geo-gallery-nature">
        {[
          { src: IMG_NATURE_1, captionFr: "Sites mégalithiques · pilier d'ancrage tellurique", captionEn: "Megalithic sites · telluric anchor pillar" },
          { src: IMG_NATURE_2, captionFr: "Lacs & montagnes · stabilisation aquatique", captionEn: "Lakes & mountains · aquatic stabilisation" },
        ].map((p, i) => (
          <Reveal key={i} direction="up" delay={i * 0.15} duration={0.9} amount={0.15}>
            <figure
              className="group relative overflow-hidden rounded-2xl border border-[#D4AF37]/25 bg-[#0F1A3A] aspect-square"
              data-testid={`geo-nature-${i + 1}`}
            >
              <img
                src={p.src}
                alt={isFr ? p.captionFr : p.captionEn}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0F1A3A] via-[#0F1A3A]/40 to-transparent" />
              <figcaption className="absolute bottom-3 left-3 right-3">
                <span className="block text-[11px] text-amber-glow font-display leading-snug">
                  {isFr ? p.captionFr : p.captionEn}
                </span>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>

      <p className="mt-4 text-xs text-slate-500 italic">
        {isFr
          ? "Visuels originaux FACOW360 — illustration de l'approche d'Alchimie Digitale. Reproduction interdite sans autorisation."
          : "Original FACOW360 visuals — illustrating the Digital Alchemy approach. Reproduction prohibited without authorisation."}
      </p>

      {/* ============================ NOTE & CTA ============================ */}

      <TransparencyNote>
        {isFr
          ? "Pour mes expertises de géobiologie, j'implémente des moyens techniques et sensibles sans garantir de résultats sur des pathologies médicales. Ma méthode reste un accompagnement, jamais une substitution à un suivi médical."
          : "For my geobiology expertises, I implement technical and sensitive means without guaranteeing results on medical conditions. My method remains an accompaniment, never a substitute for medical follow-up."}
      </TransparencyNote>

      <TravelSection pillar="geobiologie" />

      <div className="mt-12 flex flex-wrap gap-3">
        <Link to="/devis" className="btn-gold" data-testid="geo-cta-devis">
          {isFr ? "Demander un devis" : "Request a quote"}
        </Link>
        <Link to="/bilan" className="btn-primary" data-testid="geo-cta-bilan">
          {isFr ? "Bilan de Vitalité (19 €)" : "Vitality Assessment (€19)"}
        </Link>
        <Link to="/sentinelles-vertes" className="btn-primary" data-testid="geo-cta-sentinelles">
          {isFr ? "Les Sentinelles Vertes →" : "The Green Sentinels →"}
        </Link>
      </div>
    </PageShell>
  );
}
